import {
  PAS_ARRONDI_CENTIMES,
  POURCENTAGE_LOT_MAX,
  POURCENTAGE_LOT_MIN,
  pointsDeBaseDe,
  type ActionLot,
  type ActionLotOeuvres,
  type Devise,
  type SensArrondi,
  type StatutCibleLot,
} from '@artosera/shared';

/** « Sans lieu » : l'œuvre quitte son lieu (`lieuId: null`). */
export const SANS_LIEU = '__sans_lieu__';

/** Ce que la personne a saisi à l'étape « Paramètres ». Rien n'est présélectionné. */
export interface ParametresLot {
  statut: StatutCibleLot | '';
  raison: string;
  lieu: string;
  publication: 'publier' | 'retirer' | '';
  expositionId: string;
  selectionId: string;
  prixMode: 'pourcentage' | 'montant';
  prixSens: 'hausse' | 'baisse' | '';
  prixValeur: string;
  devise: Devise;
  pas: string;
  sensArrondi: SensArrondi | '';
}

export const PARAMETRES_VIDES: ParametresLot = {
  statut: '',
  raison: '',
  lieu: '',
  publication: '',
  expositionId: '',
  selectionId: '',
  prixMode: 'pourcentage',
  prixSens: '',
  prixValeur: '',
  devise: 'EUR',
  pas: '',
  sensArrondi: '',
};

const POURCENTAGE = /^\d{1,4}([.,]\d{1,2})?$/;
const MONTANT = /^\d{1,9}([.,]\d{1,2})?$/;

/**
 * Euros saisis → centimes entiers, sans flottant : « 12,5 » donne 1250.
 * `null` si la saisie n'est pas un montant strictement positif.
 */
export function centimesDeSaisie(saisie: string): number | null {
  const texte = saisie.trim();
  if (!MONTANT.test(texte)) return null;
  const [euros = '0', centimes = ''] = texte.replace(',', '.').split('.');
  const total = Number(euros) * 100 + Number(centimes.padEnd(2, '0'));
  return total > 0 ? total : null;
}

/** Le pourcentage saisi (positif), ou `null` s'il est hors bornes une fois signé. */
export function pourcentageDeSaisie(saisie: string, baisse: boolean): number | null {
  const texte = saisie.trim();
  if (!POURCENTAGE.test(texte)) return null;
  const valeur = Number(texte.replace(',', '.'));
  if (!(valeur > 0)) return null;
  const signe = baisse ? -valeur : valeur;
  if (signe < POURCENTAGE_LOT_MIN || signe > POURCENTAGE_LOT_MAX) return null;
  return pointsDeBaseDe(signe) === null ? null : signe;
}

/** La saisie du prix est-elle renseignée mais inutilisable ? Pour l'aide sous le champ. */
export function saisiePrixInvalide(p: ParametresLot): boolean {
  if (p.prixValeur.trim() === '') return false;
  return p.prixMode === 'pourcentage'
    ? pourcentageDeSaisie(p.prixValeur, p.prixSens === 'baisse') === null
    : centimesDeSaisie(p.prixValeur) === null;
}

/** L'action à envoyer, ou `null` tant que les paramètres sont incomplets. */
export function construireAction(type: ActionLotOeuvres, p: ParametresLot): ActionLot | null {
  const raison = p.raison.trim();
  switch (type) {
    case 'statut':
      return p.statut === '' ? null : { type, vers: p.statut, ...(raison ? { raison } : {}) };
    case 'localisation':
      return p.lieu === '' ? null : { type, lieuId: p.lieu === SANS_LIEU ? null : p.lieu };
    case 'publication':
      return p.publication === '' ? null : { type, visible: p.publication === 'publier' };
    case 'exposition':
      return p.expositionId === '' ? null : { type, expositionId: p.expositionId };
    case 'selection':
      return p.selectionId === '' ? null : { type, selectionId: p.selectionId };
    case 'archivage':
      return { type, ...(raison ? { raison } : {}) };
    case 'prix': {
      const pas = PAS_ARRONDI_CENTIMES.find((valeur) => String(valeur) === p.pas);
      if (pas === undefined || p.sensArrondi === '' || p.prixSens === '') return null;
      const arrondi = { pasCentimes: pas, sens: p.sensArrondi };
      const baisse = p.prixSens === 'baisse';
      if (p.prixMode === 'pourcentage') {
        const pourcentage = pourcentageDeSaisie(p.prixValeur, baisse);
        return pourcentage === null
          ? null
          : { type, regle: { mode: 'pourcentage', pourcentage }, arrondi };
      }
      const centimes = centimesDeSaisie(p.prixValeur);
      return centimes === null
        ? null
        : {
            type,
            regle: {
              mode: 'montant',
              deltaCentimes: baisse ? -centimes : centimes,
              devise: p.devise,
            },
            arrondi,
          };
    }
  }
}
